import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { BellIcon, BriefcaseIcon, CalendarIcon, GraduationCapIcon, HomeIcon, KeyRoundIcon, ShieldAlertIcon, StickyNoteIcon, StoreIcon, UserIcon, WalletIcon } from 'lucide-react';
import { useNotifications, isAlwaysSent } from '../../contexts/NotificationContext';
import type { NotificationDomain } from '../../types/notifications';
const ICONS: Record<NotificationDomain, React.ReactNode> = {
  security: <KeyRoundIcon className="h-4 w-4" data-id="element-5204" data-mp="1al3ghna6svd" />,
  money: <WalletIcon className="h-4 w-4" data-id="element-5205" data-mp="59kduhrs7hu7" />,
  safety: <ShieldAlertIcon className="h-4 w-4" data-id="element-5206" data-mp="58r73gstosc2" />,
  admissions: <GraduationCapIcon className="h-4 w-4" data-id="element-5207" data-mp="a9f7gt2xyliy" />,
  academics: <GraduationCapIcon className="h-4 w-4" data-id="element-5208" data-mp="a72gjqf7h0tr" />,
  housing: <HomeIcon className="h-4 w-4" data-id="element-5209" data-mp="1go7fmbub2dzk" />,
  marketplace: <StoreIcon className="h-4 w-4" data-id="element-5210" data-mp="1grbv45d80w5j" />,
  community: <CalendarIcon className="h-4 w-4" data-id="element-5211" data-mp="1lnizhtpyxzld" />,
  opportunities: <BriefcaseIcon className="h-4 w-4" data-id="element-5212" data-mp="1lq55vq14i920" />,
  campagift: <WalletIcon className="h-4 w-4" data-id="element-5213" data-mp="19tkwrozpv8kd" />,
  services: <GraduationCapIcon className="h-4 w-4" data-id="element-5214" data-mp="19s9slo2czysq" />,
  notes: <StickyNoteIcon className="h-4 w-4" data-id="element-5215" data-mp="14wkqbcyyg3sq" />,
  account: <UserIcon className="h-4 w-4" data-id="element-5216" data-mp="14torlmfa9b38" />
};

/**
 * A preview, not the record. It shows the few most important unread items and
 * hands off to the notification centre for everything else.
 */
export function NotificationBell() {
  const {
    inbox,
    unreadCount,
    markAllRead,
    markRead
  } = useNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function onClick(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);
  const preview = inbox.slice(0, 5);
  return <div className="relative" ref={ref} data-id="element-5217" data-mp="36flfp1laulq">
      <button type="button" onClick={() => setOpen(value => !value)} aria-label={`Notifications, ${unreadCount} unread`} aria-expanded={open} className="relative flex h-11 w-11 items-center justify-center rounded-lg border border-line-strong bg-surface text-ink-muted transition-colors duration-150 ease-out hover:border-ink-subtle hover:text-ink" data-id="element-5218" data-mp="37gw3sep1f2x">
        <BellIcon className="h-5 w-5" data-id="element-5219" data-mp="3kpnvxgl58c9o" />
        {unreadCount > 0 && <span className="cz-num absolute right-0.5 top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-danger-700 px-1 text-caption font-semibold text-white" data-id="element-5220" data-mp="c8jed9or4757">
            {unreadCount}
          </span>}
      </button>

      <AnimatePresence data-id="element-5221" data-mp="2izsdv5qrx64">
        {open && <motion.div initial={{
        opacity: 0,
        y: -4,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: -4,
        scale: 0.98
      }} transition={{
        duration: 0.15,
        ease: [0.23, 1, 0.32, 1]
      }} className="absolute right-0 z-40 mt-2 w-[min(24rem,calc(100vw-2rem))] rounded-card border border-line bg-surface shadow-pop" data-id="element-5222" data-mp="7ht5hmh49m5r">
            <header className="flex items-center justify-between gap-3 border-b border-line px-4 py-3" data-id="element-5223" data-mp="15cdlgd8kskvt">
              <h2 className="text-body font-semibold text-ink" data-id="element-5224" data-mp="1j3ihz6gncx1x">Notifications</h2>
              <button type="button" onClick={markAllRead} disabled={unreadCount === 0} className="text-body-sm font-medium text-brand-700 transition-colors duration-150 ease-out hover:text-brand-800 disabled:text-ink-subtle" data-id="element-5225" data-mp="1e4fnuh27gb8q">
                Mark all read
              </button>
            </header>

            {preview.length === 0 ? <p className="px-4 py-5 text-body-sm text-ink-muted" data-id="element-5226" data-mp="vdemjmdm43m0">
                Nothing yet. Applications, orders, group activity and reminders you set all report
                here.
              </p> : <ul className="max-h-96 divide-y divide-line overflow-y-auto" data-id="element-5227" data-mp="26sbfh7cx3cyn">
                {preview.map(item => <li key={item.id} data-id="element-5228" data-mp="gdu3nef1sk2v">
                    <Link to={item.to ?? '/notifications'} onClick={() => {
              markRead(item.id);
              setOpen(false);
            }} className="flex w-full gap-3 px-4 py-3 text-left transition-colors duration-150 ease-out hover:bg-canvas" data-id="element-5229" data-mp="laaqm5aigsed">
                      <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${isAlwaysSent(item.domain) ? 'bg-danger-50 text-danger-700' : 'bg-canvas text-ink-muted'}`} data-id="element-5230" data-mp="149oogybd2na5">
                        {ICONS[item.domain]}
                      </span>
                      <span className="min-w-0 flex-1" data-id="element-5231" data-mp="1mzej9lf4j922">
                        <span className="flex items-start gap-2" data-id="element-5232" data-mp="1mxtkfjls13ye">
                          <span className="flex-1 text-body-sm font-medium text-ink" data-id="element-5233" data-mp="37js08k80jwj1">
                            {item.title}
                          </span>
                          {!item.read && <span aria-hidden="true" className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-brand-700" data-id="element-5234" data-mp="2yi25p5iu4hh1" />}
                        </span>
                        <span className="mt-0.5 block text-body-sm text-ink-muted" data-id="element-5235" data-mp="3m6uvslcrzv3q">
                          {item.detail}
                        </span>
                        <span className="cz-num mt-1 block text-caption text-ink-subtle" data-id="element-5236" data-mp="3hea3xpdfjs2u">
                          {item.time}
                        </span>
                      </span>
                    </Link>
                  </li>)}
              </ul>}

            <footer className="border-t border-line px-4 py-3" data-id="element-5237" data-mp="8z9xolh9no8o">
              <Link to="/notifications" onClick={() => setOpen(false)} className="text-body-sm font-medium text-brand-700 transition-colors duration-150 ease-out hover:text-brand-800" data-id="element-5238" data-mp="rwjy9rsyowxq">
                Open notification centre
              </Link>
              <p className="mt-1.5 text-caption text-ink-subtle" data-id="element-5239" data-mp="n4sf42c4xthm">
                Safety, security and money always arrive, even when other topics are muted.
              </p>
            </footer>
          </motion.div>}
      </AnimatePresence>
    </div>;
}